import React, { useEffect, useState } from 'react';
import { ArrowLeft, AlertCircle, Calendar } from 'lucide-react';
import AttendanceCalendar from '../attendance/AttendanceCalendar';
import AttendanceSummary from '../attendance/AttendanceSummary';
import AttendanceRecords from '../attendance/AttendanceRecords';
import ClassmatesList from './ClassmatesList';
import AssignmentCard from '../assignments/AssignmentCard';
import { getInitials } from '../../utils/helpers';
import apiService from '../../api/apiService';
import { getApiServiceForGrade, isPreparatoryGrade } from '../../api/apiServiceFactory';
import { API_BASE_URL } from '../../utils/constants';

/**
 * Component for displaying class details
 * Uses different API services based on grade level
 *
 * @param {Object} props
 * @param {Object} props.selectedClass - The selected class data
 * @param {Function} props.handleBack - Function to navigate back
 * @param {Function} props.handleAssignmentSelect - Function to handle assignment selection
 * @param {Function} props.handleEnroll - Function to handle class enrollment
 * @param {Function} props.handleDrop - Function to handle class dropping
 * @param {Object} props.notification - Notification state
 * @param {Function} props.showNotification - Function to show notifications
 */

const ClassDetailsView = ({
    selectedClass,
    handleBack,
    handleAssignmentSelect,
    handleEnroll,
    handleDrop,
    notification,
    showNotification
}) => {
    const [classTab, setClassTab] = useState("assignments"); // assignments, attendance
    const [classmates, setClassmates] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);

    // Attendance-related state
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [attendanceSummary, setAttendanceSummary] = useState(null);
    const [attendanceError, setAttendanceError] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    // Determine which API service to use based on grade level
    const grade = selectedClass?.grade ? parseInt(selectedClass.grade, 10) : 1;
    const currentApiService = getApiServiceForGrade(grade);
    const isPreparatory = isPreparatoryGrade(grade);

    // Fetch class data when selected class changes
    useEffect(() => {
        if (selectedClass) {
            fetchClassData(selectedClass.class_id);
        }
    }, [selectedClass]);

    // Fetch class data (classmates, assignments, and attendance)
    const fetchClassData = async (classId) => {
        setLoading(true);
        try {
            // Fetch classmates using regular API (assuming this is the same for both stages)
            const classmatesData = await apiService.getClassmates(classId);
            console.log(classmatesData);

            // Fetch assignments using the appropriate API service based on grade
            const assignmentsData = await currentApiService.getClassAssignments(classId);

            setClassmates(classmatesData);
            setAssignments(assignmentsData);

            // Also fetch attendance data if the class is enrolled
            if (selectedClass.is_enrolled) {
                setAttendanceError(null);
                try {
                    const [attendanceData, summaryData] = await Promise.all([
                        apiService.getAttendanceRecords(classId),
                        apiService.getAttendanceSummary(classId)
                    ]);

                    setAttendanceRecords(attendanceData);
                    setAttendanceSummary(summaryData);
                } catch (attendanceErr) {
                    console.error("Error fetching attendance data:", attendanceErr);
                    setAttendanceError(attendanceErr.message || "Failed to load attendance data.");
                }
            }
        } catch (err) {
            // showNotification(`Failed to fetch class data: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle attendance-related functions
    const handlePreviousMonth = () => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(newDate.getMonth() - 1);
        setCurrentMonth(newDate);
    };

    const handleNextMonth = () => {
        const newDate = new Date(currentMonth);
        newDate.setMonth(newDate.getMonth() + 1);
        setCurrentMonth(newDate);
    };

    const handleSelectDate = (date) => {
        setSelectedDate(date);
    };

    if (!selectedClass) return null;

    return (
        <div className="font-[poppins] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Header with navigation */}
            <div className="mb-10">
                <button
                    onClick={handleBack}
                    className="inline-flex items-center text-sm font-semibold text-[#D67C2C] hover:underline transition-colors"
                >
                    <ArrowLeft size={16} className="mr-1" />
                    Back to Classes
                </button>

                <div className="mt-3 flex flex-wrap items-center justify-between">
                    <div>
                        <div className="flex items-center">
                            <h1 className="text-2xl font-bold text-gray-900">{selectedClass.name}</h1>
                            {selectedClass.is_enrolled ? (
                                <span
                                    className="ml-3 py-1 px-2.5 text-xs font-medium rounded-full bg-green-100 text-green-800">
                                    Enrolled
                                </span>
                            ) : (
                                <span
                                    className="ml-3 py-1 px-2.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                    Not Enrolled
                                </span>
                            )}
                            {selectedClass.grade && (
                                <span
                                    className="ml-2 py-1 px-2.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                                    Grade {selectedClass.grade}
                                </span>
                            )}
                            {isPreparatory && (
                                <span
                                    className="ml-2 py-1 px-2.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                    Preparatory
                                </span>
                            )}
                        </div>
                        <p className="mt-1 text-gray-600">
                            {selectedClass.description || "No description provided"}
                        </p>
                        <div className="mt-2 flex items-center">

                            {selectedClass.teacher.profile_picture_url ? (
                                <img
                                    src={API_BASE_URL + selectedClass.teacher.profile_picture_url}
                                    alt={selectedClass.teacher.name}
                                    className="h-10 w-10 rounded-full object-cover border-2 border-[#F7D1B6]"
                                />
                            ) : (
                                <div
                                    className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-sm text-indigo-800 font-medium">
                                    {getInitials(selectedClass.teacher.name)}
                                </div>
                            )}

                            <p className="ml-2 text-sm text-gray-600">
                                Teacher: <span className="font-medium">{selectedClass.teacher.name}</span>
                            </p>
                        </div>
                    </div>

                    {/*<div className="mt-4 sm:mt-0">*/}
                    {/*    {selectedClass.is_enrolled ? (*/}
                    {/*        <button*/}
                    {/*            onClick={(e) => handleDrop(selectedClass.class_id, e)}*/}
                    {/*            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"*/}
                    {/*        >*/}
                    {/*            <LogOut size={16} className="mr-2"/>*/}
                    {/*            Leave Class*/}
                    {/*        </button>*/}
                    {/*    ) : (*/}
                    {/*        <button*/}
                    {/*            onClick={(e) => handleEnroll(selectedClass.class_id, e)}*/}
                    {/*            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"*/}
                    {/*        >*/}
                    {/*            <LogIn size={16} className="mr-2"/>*/}
                    {/*            Join Class*/}
                    {/*        </button>*/}
                    {/*    )}*/}
                    {/*</div>*/}
                </div>
            </div>

            {/* Tab Navigation (only show if enrolled) */}
            {selectedClass.is_enrolled && (
                <div className="mb-6 border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setClassTab("assignments")}
                            className={`${classTab === "assignments"
                                    ? "border-indigo-500 text-indigo-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Activities
                        </button>
                        <button
                            onClick={() => setClassTab("attendance")}
                            className={`${classTab === "attendance"
                                    ? "border-indigo-500 text-indigo-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
                        >
                            Attendance
                        </button>
                    </nav>
                </div>
            )}

            {/* Loading state */}
            {loading && (
                <div className="flex justify-center items-center py-12">
                    <div
                        className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
                </div>
            )}

            {/* Class Content (based on selected tab) */}
            {!loading && (
                <>
                    {/* Assignments Tab */}
                    {(!selectedClass.is_enrolled || classTab === "assignments") && (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Assignments Section */}
                            <div className="lg:col-span-2">
                                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                                    <div className="px-4 py-5 sm:px-6 bg-gray-50">
                                        <h2 className="text-lg leading-6 font-medium text-gray-900">Activities</h2>
                                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                                            Tasks and Activities for this class
                                        </p>
                                    </div>

                                    {!selectedClass.is_enrolled ? (
                                        <div className="p-8 text-center">
                                            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">Not enrolled in this
                                                class</h3>
                                            <p className="text-gray-500 mb-6">
                                                You need to join this class to see activities.
                                            </p>
                                            {/*<button*/}
                                            {/*    onClick={(e) => handleEnroll(selectedClass.class_id, e)}*/}
                                            {/*    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"*/}
                                            {/*>*/}
                                            {/*    <LogIn size={16} className="mr-2"/>*/}
                                            {/*    Join Class*/}
                                            {/*</button>*/}
                                        </div>
                                    ) : assignments.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                                            <h3 className="text-lg font-medium text-gray-900 mb-2">No activities
                                                yet</h3>
                                            <p className="text-gray-500">
                                                There are no activities for this class yet.
                                            </p>
                                        </div>
                                    ) : (
                                        <ul className="divide-y divide-gray-200">
                                            {assignments.map((assignment) => (
                                                <li key={assignment.assignment_id}>
                                                    <AssignmentCard
                                                        assignment={assignment}
                                                        onSelect={() => handleAssignmentSelect(assignment, selectedClass)}
                                                        isPreparatory={isPreparatory}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            {/* Classmates Section */}
                            <div>
                                <ClassmatesList
                                    classmates={classmates}
                                    isEnrolled={selectedClass.is_enrolled}
                                />
                            </div>
                        </div>
                    )}

                    {/* Attendance Tab */}
                    {selectedClass.is_enrolled && classTab === "attendance" && (
                        <>
                            {/* Error banner */}
                            {attendanceError && (
                                <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                    <span>{attendanceError}</span>
                                </div>
                            )}

                            {/* Attendance Summary Section */}
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Attendance Overview */}
                                <div className="lg:col-span-1">
                                    <AttendanceSummary attendanceSummary={attendanceSummary} />
                                </div>

                                {/* Calendar View */}
                                <div className="lg:col-span-2">
                                    <AttendanceCalendar
                                        currentMonth={currentMonth}
                                        selectedDate={selectedDate}
                                        attendanceRecords={attendanceRecords}
                                        handlePreviousMonth={handlePreviousMonth}
                                        handleNextMonth={handleNextMonth}
                                        handleSelectDate={handleSelectDate}
                                    />
                                </div>
                            </div>

                            {/* Attendance Records Table */}
                            <AttendanceRecords attendanceRecords={attendanceRecords} />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ClassDetailsView;